import _ from 'lodash';
import { useContext } from 'react';
import { useApiFetchGetRequest } from 'react-scale/hooks/useApiFetchGetRequest';
import { IS_FEDERAL } from '@scale/frontend-lib/constants';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import { SupplementalAssignmentData } from 'react-scale/hooks/useSupplementedQueueAssignment';
import UserContext from 'react-scale/components/ui/UserContext';
import {
  ConfigJson,
  twoDTypes,
  ReviewLevel,
  defaultSpotterReviewLevel,
} from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import { LoggedInUser } from '@scale/shared/types/user';
import { useUnleashBoolean } from './useUnleashFeatureFlag';
import { useCommonFeatureFlags } from './useCommonFeatureFlags';

const useSupplementedMultiTaskResponseAssignment = (
  assignmentJson?: AssignmentJson,
): AssignmentJson | undefined => {
  const commonFeatureFlags = useCommonFeatureFlags(assignmentJson);

  const shortCircuit =
    !_.includes(twoDTypes, assignmentJson?.type) &&
    assignmentJson?.type !== TaskType.DocumentTranscription &&
    assignmentJson?.type !== TaskType.Chat &&
    assignmentJson?.type !== TaskType.ChatExperimental &&
    assignmentJson?.type !== TaskType.ChatV2;

  const canLeaveInlineCommentsFlag =
    useUnleashBoolean('can-leave-inline-comments', {}, { shortCircuit }) ||
    assignmentJson?.isScaleStudio;

  const canRejectWithChangesFlag = useUnleashBoolean(
    'can-reject-with-changes',
    { projectId: assignmentJson?.projectId },
    { shortCircuit },
  );

  const canRejectWithChanges =
    assignmentJson && (canRejectWithChangesFlag || !!localStorage.canRejectWithChanges);

  const projectConfig = useApiFetchGetRequest<ConfigJson>(
    `internal/config/${assignmentJson?.projectId}`,
    { shouldShortCircuit: shortCircuit },
  );

  const isAtLeastReviewLevel = (reviewLevel: ReviewLevel, assignmentJson?: AssignmentJson) => {
    return (
      reviewLevel === -1 ||
      (assignmentJson?.reviewLevel && assignmentJson.reviewLevel >= reviewLevel) ||
      (assignmentJson &&
        reviewLevel === ReviewLevel.QIRLeadAuditor &&
        assignmentJson.reviewLevel === ReviewLevel.QIRLeadAuditor) ||
      (assignmentJson &&
        reviewLevel === ReviewLevel.QIRAuditor &&
        (assignmentJson.reviewLevel === ReviewLevel.QIRAuditor ||
          assignmentJson.reviewLevel === ReviewLevel.QIRLeadAuditor))
    );
  };

  const configSpotterReviewLevel =
    projectConfig.response?.spotterReviewLevel ?? defaultSpotterReviewLevel;
  const canLeaveInlineComments =
    IS_FEDERAL &&
    (canLeaveInlineCommentsFlag ||
      isAtLeastReviewLevel(
        projectConfig.response?.inlineCommentsFromReview ?? 10,
        assignmentJson,
      ) ||
      (projectConfig.response?.isSpotterEnabled &&
        isAtLeastReviewLevel(configSpotterReviewLevel, assignmentJson) &&
        projectConfig.response?.inlineCommentsEnabled) ||
      !!localStorage.canLeaveInlineComments);

  const user = useContext(UserContext) as LoggedInUser | undefined;
  const supportData: SupplementalAssignmentData = {
    loggedUserId: user?._id,
  };

  const featureFlags = {
    ...commonFeatureFlags,
    canRejectWithChanges,
    canLeaveInlineComments,
    // upstream assignment creation may have feature flags that override the common ones
    // such as customer audit which overrides canSeeInlineComments: false
    ...assignmentJson?.featureFlags,
  };

  if (!assignmentJson) return undefined;

  return {
    ...assignmentJson,
    ...supportData,
    featureFlags,
    config: projectConfig.response,
    projectConfig: projectConfig.response,
  };
};

export default useSupplementedMultiTaskResponseAssignment;
